import * as FileSystem from "expo-file-system/legacy"

// url -> in-flight download, so concurrent callers share one download.
const inFlight = new Map()

// Short, filesystem-safe, deterministic name — not a cryptographic hash.
const hashUrl = (url) => {
    let hash = 0
    for (let i = 0; i < url.length; i++) {
        hash = (hash * 31 + url.charCodeAt(i)) | 0
    }
    return Math.abs(hash)
}

/**
 * Resolves a remote url to a local file in the cache directory: downloaded
 * once, shared by concurrent callers, read from disk on later calls.
 *
 * The download lands on a `.tmp` path and only moves to the final name once
 * complete and 2xx — downloadAsync resolves on HTTP errors, and a killed app
 * leaves partial bytes behind — so an existing final file is always a good
 * copy. A failure rejects, and the next call retries from scratch.
 */
export function downloadToCache(url, { prefix, extension }) {
    let promise = inFlight.get(url)
    if (!promise) {
        promise = download(url, `${prefix}${hashUrl(url)}${extension}`).finally(
            () => inFlight.delete(url)
        )
        inFlight.set(url, promise)
    }
    return promise
}

async function download(url, fileName) {
    // Native-only — cacheDirectory is reliably non-null there, but the type
    // allows null.
    if (!FileSystem.cacheDirectory) {
        throw new Error("downloadToCache: no cacheDirectory")
    }
    const localUri = FileSystem.cacheDirectory + fileName
    const tmpUri = `${localUri}.tmp`

    if ((await FileSystem.getInfoAsync(localUri)).exists) {
        return localUri
    }
    try {
        const { status } = await FileSystem.downloadAsync(url, tmpUri)
        if (status < 200 || status >= 300) {
            throw new Error(`downloadToCache: HTTP ${status} for ${url}`)
        }
        await FileSystem.moveAsync({ from: tmpUri, to: localUri })
        return localUri
    } catch (error) {
        await FileSystem.deleteAsync(tmpUri, { idempotent: true }).catch(() => {})
        throw error
    }
}
